/** Typed wrappers per endpoint. Components use hooks, not these directly. */

import { api } from "@/api/client";
import type {
  Activation,
  CrmConnection,
  Customer,
  CustomerImport,
  DashboardStats,
  DealDetail,
  DealDocument,
  DealScope,
  DealSummary,
  DocumentDetail,
  LoginResponse,
  MessageResponse,
  Payment,
  ProductDetail,
  ProductSummary,
  SignatureRequest,
  SignupPolicy,
  SummaryRow,
  TokenCheck,
  User,
  UserRole,
} from "@/api/types";

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>("/auth/login", { email, password }),
  me: () => api.get<User>("/auth/me"),
  users: () => api.get<User[]>("/auth/users"),

  policy: () => api.get<SignupPolicy>("/auth/signup-policy"),
  signup: (name: string, email: string, role: UserRole) =>
    api.post<MessageResponse>("/auth/signup", { name, email, role }),
  forgotPassword: (email: string) =>
    api.post<MessageResponse>("/auth/password/forgot", { email }),
  checkToken: (token: string) =>
    api.get<TokenCheck>(`/auth/password/token/${encodeURIComponent(token)}`),
  // Returns a session, so the user lands signed in rather than retyping the
  // password they just chose.
  setPassword: (token: string, password: string, passwordConfirm: string) =>
    api.post<LoginResponse>("/auth/password/set", {
      token,
      password,
      password_confirm: passwordConfirm,
    }),
  changePassword: (currentPassword: string, password: string, passwordConfirm: string) =>
    api.post<MessageResponse>("/auth/password/change", {
      current_password: currentPassword,
      password,
      password_confirm: passwordConfirm,
    }),
};

export const productsApi = {
  list: () => api.get<ProductSummary[]>("/products"),
  detail: (slug: string) => api.get<ProductDetail>(`/products/${slug}`),
};

export const customersApi = {
  search: (query: string) =>
    api.get<Customer[]>(`/customers${query ? `?q=${encodeURIComponent(query)}` : ""}`),
  upload: (file: File) => api.upload<CustomerImport>("/customers/imports", file),
  getImport: (id: string) => api.get<CustomerImport>(`/customers/imports/${id}`),
  update: (id: string, patch: Partial<Customer>) =>
    api.patch<Customer>(`/customers/${id}`, patch),
};

export interface CustomerPayload {
  company_name: string;
  street: string;
  postal_code: string;
  city: string;
  contact_name: string;
  contact_email: string;
}

export const dealsApi = {
  list: (scope: DealScope, query: string) => {
    const params = new URLSearchParams({ scope });
    if (query) params.set("q", query);
    return api.get<DealSummary[]>(`/deals?${params.toString()}`);
  },
  stats: () => api.get<DashboardStats>("/deals/stats"),
  create: () => api.post<DealDetail>("/deals"),
  detail: (id: string) => api.get<DealDetail>(`/deals/${id}`),
  setStep: (id: string, step: number) => api.put<DealDetail>(`/deals/${id}/step`, { step }),
  selectProduct: (id: string, productId: number) =>
    api.put<DealDetail>(`/deals/${id}/product`, { product_id: productId }),
  selectCustomerById: (id: string, customerId: string) =>
    api.put<DealDetail>(`/deals/${id}/customer`, { customer_id: customerId }),
  createCustomer: (id: string, customer: CustomerPayload) =>
    api.put<DealDetail>(`/deals/${id}/customer`, { customer }),
  generateContract: (id: string) => api.post<DealDocument[]>(`/deals/${id}/contract`),
  contractPreview: (id: string) => api.get<Record<string, string>>(`/deals/${id}/contract/preview`),
  contractDocument: (id: string, documentId: string) =>
    api.get<DocumentDetail>(`/deals/${id}/contract/documents/${documentId}`),
  releaseContract: (id: string) => api.post<DealDetail>(`/deals/${id}/contract/release`),
  sendSignature: (id: string, simulate = false) =>
    api.post<SignatureRequest>(
      `/deals/${id}/signature${simulate ? "?simulate=true" : ""}`,
    ),
  // Mock-provider only: the real path is the Zoho Sign webhook.
  simulateSignature: (id: string, orderIndex: number) =>
    api.post<SignatureRequest>(`/deals/${id}/signature/simulate`, { order_index: orderIndex }),
  sendPaymentLink: (id: string) => api.post<Payment>(`/deals/${id}/payment`),
  simulatePayment: (id: string) => api.post<Payment>(`/deals/${id}/payment/simulate`),
  activate: (id: string) => api.post<Activation>(`/deals/${id}/activation`),
  summary: (id: string) => api.get<{ rows: SummaryRow[] }>(`/deals/${id}/summary`),
  cancel: (id: string) => api.delete<DealDetail>(`/deals/${id}`),
};

export const crmApi = {
  list: () => api.get<CrmConnection[]>("/crm/connections"),
  prepare: (provider: string) => api.post<CrmConnection>(`/crm/connections/${provider}`),
};
